/**
 * Codici IVA dei clienti per organizzazione (esenzioni: art. 41 DL 331/93,
 * art. 8 DPR 633/72, …). Tabella vat_codes, 20261001e_client_vat_codes.sql.
 * Richiesta Say Farm; un'org senza codici non vede la funzione.
 *
 * Il cliente sceglie un codice (clients.vat_code_id) e prende la sua aliquota
 * (clients.default_vat_rate): l'offerta la applica alle righe con «Applica a
 * tutte le righe» e il preventivo stampa la dicitura (flusia-core,
 * {{deal.vat_exemption_note}}).
 */
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from '../stores/referenceDataCache';

export interface VatCode {
  id: string;
  code: string;
  description: string;
  rate: number;
  legal_note: string | null;
  nature: string | null;
  is_active: boolean;
  sort_order: number;
}

export type VatCodeInput = Omit<VatCode, 'id'> & { id?: string };

const COLUMNS = 'id, code, description, rate, legal_note, nature, is_active, sort_order';

/** Etichetta breve per tendine e card: «NI41 · Non imponibile art. 41 (0%)». */
export function vatCodeLabel(c: Pick<VatCode, 'code' | 'description' | 'rate'>): string {
  return `${c.code} · ${c.description} (${Number(c.rate)}%)`;
}

export async function listVatCodes(includeInactive = false): Promise<VatCode[]> {
  let q = supabase.from('vat_codes').select(COLUMNS).order('sort_order').order('code');
  if (!includeInactive) q = q.eq('is_active', true);
  const { data, error } = await q;
  if (error) throw error;
  return ((data || []) as VatCode[]).map(c => ({ ...c, rate: Number(c.rate) }));
}

export async function getVatCode(id: string): Promise<VatCode | null> {
  const { data, error } = await supabase.from('vat_codes').select(COLUMNS).eq('id', id).maybeSingle();
  if (error || !data) return null;
  return { ...(data as VatCode), rate: Number((data as VatCode).rate) };
}

export async function saveVatCode(input: VatCodeInput): Promise<void> {
  const row = {
    code: input.code.trim(),
    description: input.description.trim(),
    rate: Number(input.rate) || 0,
    legal_note: input.legal_note?.trim() || null,
    nature: input.nature?.trim() || null,
    is_active: input.is_active,
    sort_order: input.sort_order,
  };
  if (input.id) {
    const { error } = await supabase.from('vat_codes').update(row).eq('id', input.id);
    if (error) throw error;
    return;
  }
  const organizationId = await getCurrentUserOrganizationId();
  if (!organizationId) throw new Error('Organizzazione non trovata');
  const { error } = await supabase.from('vat_codes').insert({ ...row, organization_id: organizationId });
  if (error) throw error;
}

export async function deleteVatCode(id: string): Promise<void> {
  const { error } = await supabase.from('vat_codes').delete().eq('id', id);
  if (error) throw error;
}
